import React, {useEffect, useState} from "react";
import {Button, Card, Divider, NavBar} from "antd-mobile";
import {useNavigate} from "react-router-dom";
import networkService from "../../../services/NetworkService";
import {useNoIconToast} from "../../../hooks/useCustomToast"
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {t} from "../../../i18n";
import pickedUpDataService, {PickedUpData, uploadFailedPickUpPhotos} from "../../../services/PickedUpDataService";
import {usePickupTaskOperations} from "../store/pickupStore";
import dayjs from "dayjs";
import { getNavBarContainerStyle } from "../../../utils/deviceUtils";

interface PickUpUploadFailedProps {
}


const PickedUpFailedPage: React.FC<PickUpUploadFailedProps> = () => {
    const navigate = useNavigate();
    const [failedUploads, setFailedUploads] = useState<PickedUpData[]>([]);
    const [isOnline, setIsOnline] = useState(networkService.isNetworkAvailable());
    const {showNoIconToast} = useNoIconToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [notification, setNotification] = useState<boolean>(false);
    const { updatePickupAppointment, createUnloadRecord,handlePickupFailed,markArrivedAtStartingPoint,updateCompletedPickupAppointment } = usePickupTaskOperations();

    useEffect(() => {
        //初始化网络状态
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        networkService.addEventListener('online', handleOnline);
        networkService.addEventListener('offline', handleOffline);

        return () => {
            networkService.removeEventListener('online', handleOnline);
            networkService.removeEventListener('offline', handleOffline);
        };
    }, []);

    // 获取失败的上传数据
    useEffect(() => {
        const getPickedUpFailedData = async () => {
            try {
                setIsLoading(true);
                // 使用await正确处理异步调用
                const pickedUpFailedData = await pickedUpDataService.getAllPickedUpData();
                setFailedUploads(pickedUpFailedData);
            } catch (error) {
                console.error('Failed to fetch orders:', error);
            } finally {
                setIsLoading(false);
            }
        };

        getPickedUpFailedData();
    }, [notification]);

    // 处理Upload All点击
    const handleUploadAll = async () => {
        if (!isOnline) {
            showNoIconToast('The network connection is unavailable, please try again later','center');
            return;
        }

        if (failedUploads.length === 0) {
            showNoIconToast('There is no data to be uploaded','center');
            return;
        }

        if (isUploading) {
            return; // 防止重复点击
        }

        setIsUploading(true);
        for (const pickedUpData of failedUploads) {
            //根据type进行不同重试逻辑
            const type = pickedUpData.type;
            try {
                if (type === 'pickedUp'){
                    await updatePickupAppointment({
                        appointmentId: pickedUpData?.id ||  '0',
                        taskId: pickedUpData.taskId || '0',
                        actualParcelCount: pickedUpData.actualPickupCount || 0,
                        actualParcelUnit: pickedUpData.actualPickupUnit || 1,
                        photosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                        actualPickupDate: dayjs().format("YYYY-MM-DD"),
                        actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                        cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                    },false);
                }else if (type === 'handover'){
                    await createUnloadRecord({
                        taskId: pickedUpData?.taskId || '',
                        warehouseId: pickedUpData?.warehouseId || '',
                        status: pickedUpData?.status || 2,
                        photoS3Keys: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || []
                    },false);
                }
                else if (type === 'failed') {
                    await handlePickupFailed({
                        appointmentId: pickedUpData?.id ||  '0',
                        taskId: pickedUpData?.taskId || '',
                        failedReason: pickedUpData?.failedReason || '',
                        exceptionPhotosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                        actualPickupDate: dayjs().format("YYYY-MM-DD"),
                        actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                        cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                    },false);
                } else if(type === 'arrivedAtStartingPoint') {
                    await markArrivedAtStartingPoint({
                        taskId: pickedUpData?.taskId || '',
                        appointmentId: pickedUpData?.id || ''
                    }, false)
                } else if (type === 'failedToSec') {
                    await updateCompletedPickupAppointment ({
                        appointmentId: pickedUpData?.id || '',
                        taskId: pickedUpData?.taskId || '',
                        actualParcelCount: pickedUpData.actualPickupCount || 0,
                        actualParcelUnit: pickedUpData.actualPickupUnit || 1,
                        photosS3KeysJson: await uploadFailedPickUpPhotos(pickedUpData.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo)) || [],
                        actualPickupDate: dayjs().format("YYYY-MM-DD"),
                        actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                        cooperationWarehouseId: pickedUpData?.cooperationWarehouseId,
                    },false)
                }


                // 提交成功后删除本地缓存
                await pickedUpDataService.deletePickedUpData(pickedUpData?.id?.toString());
            } catch (error: any) {
                console.error(`提交揽收记录 ${pickedUpData?.id} 失败`, error);
                if (!error.networkError) {
                    console.log('Non-network error detected, deleting pickedUpData:', error);
                    await pickedUpDataService.deletePickedUpData(pickedUpData?.id?.toString());
                }
            } finally {
                setNotification(prev => !prev);
            }
        }
        setIsUploading(false);
    };

    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            <div {...getNavBarContainerStyle('flex-none sticky top-0 z-10 bg-white shadow-sm')}>
                <NavBar
                    onBack={() => navigate("/pickup/picking-up")}
                    right={
                        <Button
                            color='primary'
                            fill='none'
                            onClick={handleUploadAll}
                            disabled={!isOnline || failedUploads.length === 0 || isUploading}
                            loading={isUploading}
                        >
                            {t(`pages.pickUp.failed.uploadAll`)}
                        </Button>
                    }
                >
                    <strong>{t(`pages.pickUp.useMenu.failedUploads`)}</strong>
                </NavBar>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Loading...</p>
                </div>
            ) : failedUploads.length > 0 ? (
                <div className="bg-gray-100">
                    {failedUploads.map((data, index) => (
                        <Card key={data.id} className="my-2 rounded-lg">
                            <div className="flex gap-6">
                                <div className="flex flex-col w-14 items-center justify-center gap-0.5">
                                    <div className="text-3xl font-bold">
                                        {data.stopNumber}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <h3 className="text-base font-semibold">{data.channelName}</h3>
                                    <p> {t(`pages.pickUp.useMenu.failedUploads`)}</p>
                                    {data.updatedAt && (
                                        <p>{t(`pages.pickUp.failed.saveAt`)} {timestampToDate(data.updatedAt).toLocaleString()}</p>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <EmptyState description={t(`pages.pickUp.noPickUpInfo`)}/>
            )}

        </div>
    );
};

export default PickedUpFailedPage;